var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { useStripes } from "@folio/stripes/core";
import { getColumnId } from "../types/ColumnDefinition";
import { EMPTY_COLUMN_PREFS, buildColumnPrefsStorageKey, isColumnLayoutCustomized, mergeColumnWeights, mergeColumnOrder, moveColumnId, parseColumnPrefs, resolveStripesUserId, resolveVisibleColumnIds, toggleColumnVisibility, } from "../utils/columnPrefs";
var readPrefs = function (storageKey) {
    if (!storageKey) {
        return EMPTY_COLUMN_PREFS;
    }
    try {
        return parseColumnPrefs(localStorage.getItem(storageKey));
    }
    catch (_a) {
        return EMPTY_COLUMN_PREFS;
    }
};
var writePrefs = function (storageKey, prefs) {
    if (!storageKey) {
        return;
    }
    try {
        localStorage.setItem(storageKey, JSON.stringify(prefs));
    }
    catch (error) {
        console.error("Error storing column preferences:", error);
    }
};
var clearPrefs = function (storageKey) {
    if (!storageKey) {
        return;
    }
    try {
        localStorage.removeItem(storageKey);
    }
    catch (error) {
        console.error("Error clearing column preferences:", error);
    }
};
export var useColumnPrefs = function (view, columns) {
    var stripes = useStripes();
    var storageKey = view
        ? buildColumnPrefsStorageKey(resolveStripesUserId(stripes.user), view)
        : null;
    var _a = useState(function () { return readPrefs(storageKey); }), prefs = _a[0], setPrefs = _a[1];
    var loadedKey = useRef(storageKey);
    useEffect(function () {
        if (loadedKey.current === storageKey) {
            return;
        }
        loadedKey.current = storageKey;
        setPrefs(readPrefs(storageKey));
    }, [storageKey]);
    var descriptors = useMemo(function () {
        return columns.map(function (column) { return ({
            id: getColumnId(column),
            locked: column.locked,
            alwaysVisible: column.alwaysVisible,
            defaultHidden: column.defaultHidden,
        }); });
    }, [columns]);
    var visibleColumnIds = useMemo(function () {
        return storageKey
            ? resolveVisibleColumnIds(descriptors, prefs)
            : resolveVisibleColumnIds(descriptors, EMPTY_COLUMN_PREFS);
    }, [descriptors, prefs, storageKey]);
    var manageableColumns = useMemo(function () {
        var visible = new Set(visibleColumnIds);
        var orderedIds = mergeColumnOrder(descriptors, prefs.order);
        return orderedIds
            .filter(function (id) { var _a; return !((_a = descriptors.find(function (entry) { return entry.id === id; })) === null || _a === void 0 ? void 0 : _a.locked); })
            .map(function (id) {
            var column = columns.find(function (entry) { return getColumnId(entry) === id; });
            return {
                id: id,
                label: column === null || column === void 0 ? void 0 : column.name,
                visible: visible.has(id),
                hideable: !(column === null || column === void 0 ? void 0 : column.alwaysVisible),
            };
        });
    }, [columns, descriptors, prefs.order, visibleColumnIds]);
    var isCustomized = useMemo(function () { return !!storageKey && isColumnLayoutCustomized(descriptors, prefs); }, [descriptors, prefs, storageKey]);
    var persist = useCallback(function (next) {
        setPrefs(next);
        writePrefs(storageKey, next);
    }, [storageKey]);
    var toggleColumn = useCallback(function (id) {
        var descriptor = descriptors.find(function (column) { return column.id === id; });
        if (descriptor)
            persist(toggleColumnVisibility(prefs, descriptor));
    }, [descriptors, persist, prefs]);
    var moveColumn = useCallback(function (id, offset) {
        var currentOrder = manageableColumns.map(function (column) { return column.id; });
        persist(__assign(__assign({}, prefs), { order: moveColumnId(currentOrder, id, offset) }));
    }, [manageableColumns, persist, prefs]);
    var setColumnWidths = useCallback(function (widths, persistNow) {
        var next = __assign(__assign({}, prefs), { widths: mergeColumnWeights(prefs.widths, widths) });
        if (persistNow) {
            persist(next);
        }
        else {
            setPrefs(next);
        }
    }, [persist, prefs]);
    var revertColumnWidths = useCallback(function () {
        setPrefs(readPrefs(storageKey));
    }, [storageKey]);
    var resetColumns = useCallback(function () {
        setPrefs(EMPTY_COLUMN_PREFS);
        clearPrefs(storageKey);
    }, [storageKey]);
    return {
        isEnabled: !!storageKey,
        visibleColumnIds: visibleColumnIds,
        manageableColumns: manageableColumns,
        isCustomized: isCustomized,
        widths: prefs.widths,
        toggleColumn: toggleColumn,
        moveColumn: moveColumn,
        setColumnWidths: setColumnWidths,
        revertColumnWidths: revertColumnWidths,
        resetColumns: resetColumns,
    };
};
